<!-- SEO landing page (target: "pdf to csv", ~2.9K/mo, KD ~13 — maps 1:1 to CSV export). -->
<template>
  <SeoLanding
    kicker="PDF to CSV"
    h1="PDF to CSV: Turn Documents into Clean Spreadsheets"
    subtitle="Convert invoices, receipts, statements, and reports from PDF into clean CSV you can open in Excel or Sheets — with the fields already labeled, not just dumped text."
    cta-label="Convert a PDF free"
    cta-to="/signup"
    :sections="sections"
    :faqs="faqs"
    closing-title="From PDF to spreadsheet in seconds"
    closing-subtitle="Upload a PDF, get labeled CSV. Free for 5 pages a month — no credit card required."
    :related-links="relatedLinks"
  />
</template>

<script setup>
import SeoLanding from '@/components/marketing/SeoLanding.vue'
import { usePageSeo, faqJsonLd, articleJsonLd } from '@/composables/usePageSeo'

const CANONICAL = 'https://www.papersnap.io/pdf-to-csv'

const sections = [
  {
    h2: 'Why "PDF to CSV" is harder than it sounds',
    paragraphs: [
      'Most PDF-to-CSV tools just pull raw text and leave you to untangle it. That works for a clean digital table and falls apart on real business documents — invoices with headers and totals, receipts with odd layouts, or scanned pages where the "text" is actually an image. What you usually want isn\'t the words on the page, it\'s the fields: the invoice number, the line items, the amounts.',
      'Papersnap converts a PDF into structured data first — it understands what kind of document it is and labels the fields — then exports clean CSV. Digital PDFs are read directly; scans and photos go through OCR. Either way you get columns that mean something, not a wall of text.',
    ],
  },
  {
    h2: 'What you can convert to CSV',
    bullets: [
      { label: 'Invoices', text: 'invoice number, vendor, line items, tax, and total as tidy rows' },
      { label: 'Receipts', text: 'merchant, date, items, and total — ready for expense tracking' },
      { label: 'Statements', text: 'transactions and balances pulled into a spreadsheet-ready table' },
      { label: 'Reports & tables', text: 'tabular data extracted with its structure intact' },
    ],
  },
  {
    h2: 'Digital PDFs and scans both work',
    bullets: [
      { label: 'Born-digital PDFs', text: 'text is read directly for the fastest, most accurate result' },
      { label: 'Scanned PDFs & photos', text: 'OCR reads the image, so a scan converts just like a digital file' },
      { label: 'Labeled columns', text: 'you get named fields, not a raw text dump you have to clean up' },
      { label: 'JSON too', text: 'need it for an API instead of a spreadsheet? Export the same data as JSON' },
    ],
  },
]

const faqs = [
  { q: 'How do I convert a PDF to CSV?', a: 'Upload the PDF to Papersnap. It detects the document type, extracts the fields, and lets you download a clean CSV you can open in Excel or Google Sheets — no manual cleanup of raw text.' },
  { q: 'Does it work on scanned PDFs, not just digital ones?', a: 'Yes. Digital PDFs are read directly; scanned PDFs and photos go through OCR. Both produce the same labeled CSV output.' },
  { q: 'Is the PDF to CSV converter free?', a: 'Yes — 5 pages a month free, no credit card. Paid plans from $9/month add higher volume and bulk export.' },
  { q: 'Can I get JSON instead of CSV?', a: 'Yes. Every extraction is available as both CSV (for spreadsheets) and JSON (for APIs and integrations).' },
]

const relatedLinks = [
  { to: '/receipt-scanner', label: 'Receipt scanner' },
  { to: '/invoice-ocr', label: 'Invoice OCR' },
  { to: '/intelligent-character-recognition', label: 'What is ICR?' },
]

usePageSeo({
  title: 'PDF to CSV — Turn Documents into Clean Spreadsheets',
  description: 'Convert PDF invoices, receipts, and statements into clean, labeled CSV for Excel or Sheets. Digital and scanned PDFs both supported; JSON export too.',
  path: '/pdf-to-csv',
  jsonLd: [
    articleJsonLd({ headline: 'PDF to CSV: Turn Documents into Clean Spreadsheets', description: 'How to convert PDF invoices, receipts, and statements into clean labeled CSV.', url: CANONICAL, datePublished: '2026-07-08' }),
    faqJsonLd(faqs.map((f) => ({ question: f.q, answer: f.a }))),
  ],
})
</script>
